import React, { useState, useRef, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import ConversationView from './ConversationView';
import SuggestedTopicsTree from './SuggestedTopicsTree';
import { ConversationThread } from './claude/conversation_thread';

import './App.css';

function App() {
  const [openConversations, setOpenConversations] = useState<ConversationThread[]>(() => [
    new ConversationThread(),
  ]);
  const [activeConversationId, setActiveConversationId] = useState(() => openConversations.at(0)?.getId());
  const conversationsContainerRef = useRef<HTMLDivElement>(null);

  const createNewConversation = (followUpQuestion?: string) => {
    const activeConversation = openConversations.find(conv => conv.getId() === activeConversationId);
    if (!activeConversation) return;
    
    const newConversation = new ConversationThread();
    if (followUpQuestion) {
      newConversation.sendUserQuery(followUpQuestion);
    }

    activeConversation.addChildThread(newConversation);
    openConversationThread(activeConversation, newConversation);
  };

  const closeConversation = (conversationId: string) => {
    if (openConversations.length === 1) return; // Don't close the last conversation

    // Find the index of the conversation to close
    const conversationIndex = openConversations.findIndex(conv => conv.getId() === conversationId);
    if (conversationIndex === -1) return;

    // Close this conversation and all child topics (everything after this index)
    const updatedConversations = openConversations.slice(0, conversationIndex);
    setOpenConversations(updatedConversations);

    // Set the active conversation to the last remaining one
    if (updatedConversations.length > 0) {
      setActiveConversationId(updatedConversations[updatedConversations.length - 1].getId());
    }
  };

  const openConversationThread = (parentConversationThread: ConversationThread, childConversationThread: ConversationThread,) => {
    // We assume that the parent converstaion must be open in order to invoke the child thread.
    const parentConversationIndex = openConversations.findIndex(conv => conv.getId() === parentConversationThread.getId());
    if (parentConversationIndex === -1) return;

    // Replace all conversations in the open set after the parent conversation with the new child conversation.
    const updatedConversations = openConversations.slice(0, parentConversationIndex + 1);
    setOpenConversations([...updatedConversations, childConversationThread]);

    // Set the active conversation to the newly added conversation thread.
    setActiveConversationId(childConversationThread.getId());
  }

  useEffect(() => {
   if (conversationsContainerRef.current) {
      const activeIndex = openConversations.findIndex(conv => conv.getId() === activeConversationId);
      const containerWidth = conversationsContainerRef.current.offsetWidth;
      const conversationWidth = 900; // matches CSS chat-window width
      const scrollLeft = (activeIndex * conversationWidth) - (containerWidth - conversationWidth) / 2;

      conversationsContainerRef.current.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: 'smooth'
      });
    }
  }, [activeConversationId, openConversations, openConversations.length]);

  return (
    <div className="App">
      <div className="conversations-container" ref={conversationsContainerRef}>
        {openConversations.map((conversation, index) => (
          <div className="conversation-container" key={conversation.getId()}>
            <ConversationView
              conversationThread={conversation}
              openConversationThread={openConversationThread}
              onFocus={() => setActiveConversationId(conversation.getId())}
              onClose={() => closeConversation(conversation.getId())}
              // Don't allow the user to close the root topic.
              showCloseButton={index > 0}
              className={conversation.getId() === activeConversationId ? 'focused' : ''}
            />
            {conversation.getId() === activeConversationId && (
              <SuggestedTopicsTree 
                conversationThread={conversation}
                onCreateNewConversation={createNewConversation}
              />
            )}
          </div>
        ))}
      </div>
      <Toaster
        position="bottom-center"
        toastOptions={{
          duration: 5000,
          style: {
            background: '#bf0e01ff',
            color: 'white',
            fontWeight: '500',
          },
        }}
      />
    </div>
  );
}

export default App;
